import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  LEGENDS,
  regionToLegend,
} from '../onboarding-flow/keyboard-illustration/legends';
import {
  getUserPlatform,
  type DictationHotkeyPlatform,
} from '../onboarding/hotkey/utils';
import { layoutDisplayLabel } from '../onboarding/keyboard/layoutDisplayLabel';
import { useKeyboardLayout } from '../onboarding/keyboard/useKeyboardLayout';
import { formatChordTokens } from './platformKeyNames';

const BACKQUOTE_CHAR = '`';
const INTL_YEN_CANONICAL = 'IntlYen';
const INTL_YEN_CHAR_DEFAULT = '¥';
const BRACKET_RIGHT_CANONICAL = 'BracketRight';
const BRACKET_RIGHT_CHAR_DEFAULT = '£';

/**
 * Render a chord as display tokens with layout-aware glyph substitution.
 *
 * Canonical W3C `KeyboardEvent.code` names (desktop) and legacy web tokens
 * are mapped through `navigator.keyboard.getLayoutMap()` so AZERTY/QWERTZ
 * users see the character printed on their key, not the QWERTY physical label.
 * When the layout API is unavailable, falls back to the same onboarding legend
 * the keyboard illustration uses (`LEGENDS`). Web keeps hardcoded fallbacks for
 * region-specific defaults (`IntlYen` → ¥, `BracketRight` → £).
 */
export const useChordDisplayTokens = (
  chord: readonly string[],
  platformOverride?: DictationHotkeyPlatform
): string[] => {
  const platform = platformOverride ?? getUserPlatform();
  const { layout, layoutMap } = useKeyboardLayout();
  const { formatMessage } = useIntl();
  const legend = LEGENDS[regionToLegend(layout)];

  return useMemo(() => {
    const tokens = formatChordTokens([...chord], platform, formatMessage);

    return tokens.map((token: string, index: number): string => {
      const chordToken = chord[index];
      if (chordToken === undefined) return token;

      const code = chordToken === BACKQUOTE_CHAR ? 'Backquote' : chordToken;
      const layoutLabel = layoutDisplayLabel(code, layoutMap, token);
      if (layoutLabel !== token) return layoutLabel;

      const legendLabel = legend[code]?.label;
      if (legendLabel) return legendLabel;

      if (platform !== 'web') return token;
      if (chordToken === INTL_YEN_CANONICAL) return INTL_YEN_CHAR_DEFAULT;
      if (chordToken === BRACKET_RIGHT_CANONICAL) return BRACKET_RIGHT_CHAR_DEFAULT;
      return token;
    });
  }, [chord, layoutMap, legend, platform, formatMessage]);
};
